import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { LoaderIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import z from "zod";
import { PageHeader } from "~/components/PageHeader";
import { Button } from "~/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { PhoneInput } from "~/components/ui/phone-input";
import { Label } from "~/components/ui/label";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { getUserSession } from "~/lib/api/auth.api";
import { authClient, createAuthError, extractAuthErrorMessage } from "~/lib/better-auth/client";
import {
  NotificationSettingsSchema,
  UpdateUserSchema,
} from "~/lib/zod-schemas/auth.schema";
import { Checkbox } from "~/components/ui/checkbox";
import { Switch } from "~/components/ui/switch";

export const Route = createFileRoute("/dashboard/user")({
  component: RouteComponent,
  loader: async () => {
    const sessionData = await getUserSession();
    return sessionData?.user || null;
  },
});

function getChangeEmailErrorMessage(error: unknown): string {
  const msg = extractAuthErrorMessage(error, "");
  if (msg.toLowerCase().includes("email is the same") || msg.includes("Email is the same")) {
    return "Die neue E-Mail-Adresse ist identisch mit der aktuellen.";
  }
  if (msg.includes("Couldn't update your email") || msg.toLowerCase().includes("already exists")) {
    return "Diese E-Mail-Adresse wird bereits verwendet.";
  }
  return msg || "Die E-Mail-Adresse konnte nicht geändert werden. Bitte versuchen Sie es erneut.";
}

function RouteComponent() {
  const user = Route.useLoaderData();
  const queryClient = useQueryClient();
  const router = useRouter();
  const [newEmail, setNewEmail] = useState("");
  const [showChangeEmailForm, setShowChangeEmailForm] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
    if (params.get("emailChanged") === "1") {
      toast.success("E-Mail-Adresse wurde geändert");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const form = useForm<z.infer<typeof UpdateUserSchema>>({
    resolver: zodResolver(UpdateUserSchema),
    defaultValues: {
      company: user?.company || "",
      salutation:
        user?.salutation === "frau" ||
          user?.salutation === "herr" ||
          user?.salutation === "divers"
          ? user?.salutation
          : "frau",
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      telephone: user?.telephone || "",
      preferredLanguage: user?.preferredLanguage || "german",
    },
  });

  const notificationForm = useForm<z.infer<typeof NotificationSettingsSchema>>({
    resolver: zodResolver(NotificationSettingsSchema),
    defaultValues: {
      emailNotification: user?.emailNotification ?? false,
      desktopNotification: user?.desktopNotification ?? false,
      mobilePushNotification: user?.mobilePushNotification ?? false,
      newLocationsInMyCity: user?.newLocationsInMyCity ?? false,
      frequency:
        user?.notificationFrequency === "weekly" ? "weekly" : "monthly",
    },
  });

  useEffect(() => {
    if (!user) return;
    notificationForm.reset({
      emailNotification: user.emailNotification ?? false,
      desktopNotification: user.desktopNotification ?? false,
      mobilePushNotification: user.mobilePushNotification ?? false,
      newLocationsInMyCity: user.newLocationsInMyCity ?? false,
      frequency:
        user.notificationFrequency === "weekly" ? "weekly" : "monthly",
    });
  }, [user?.id, user?.emailNotification, user?.desktopNotification, user?.mobilePushNotification, user?.newLocationsInMyCity, user?.notificationFrequency]);

  const handleSubmit = async (values: z.infer<typeof UpdateUserSchema>) => {
    try {
      await updateUserMutation.mutateAsync(values);
      form.reset();
    } catch (err) {
      console.error("Caught mutation error:", err);
    }
  };

  const handleNotificationSubmit = async (
    values: z.infer<typeof NotificationSettingsSchema>
  ) => {
    try {
      await updateNotificationMutation.mutateAsync(values);
    } catch (err) {
      console.error("Caught notification update error:", err);
    }
  };

  const updateUser = async (data: UpdateUserSchema) => {
    const { data: response, error } = await authClient.updateUser({
      company: data.company,
      salutation: data.salutation,
      firstName: data.firstName,
      lastName: data.lastName,
      telephone: data.telephone,
      preferredLanguage: data.preferredLanguage,
      name: `${data.firstName} ${data.lastName}`,
    });
    if (error) {
      throw createAuthError(error, "An error occurred while updating profile. Please try again.", "user-update");
    }
    return response;
  };

  const updateUserMutation = useMutation({
    mutationFn: updateUser,
    onSuccess: async (_, variables) => {
      queryClient.resetQueries();
      await router.invalidate();
      form.reset({ ...variables });
      toast.success("Benutzerkonto erfolgreich aktualisiert");
    },
  });

  const updateNotificationMutation = useMutation({
    mutationFn: async (data: z.infer<typeof NotificationSettingsSchema>) => {
      const { data: response, error } = await authClient.updateUser({
        emailNotification: data.emailNotification,
        desktopNotification: data.desktopNotification,
        mobilePushNotification: data.mobilePushNotification,
        newLocationsInMyCity: data.newLocationsInMyCity,
        notificationFrequency: data.frequency,
      });
      if (error) {
        throw createAuthError(
          error,
          "Benachrichtigungseinstellungen konnten nicht gespeichert werden.",
          "user-update"
        );
      }
      return response;
    },
    onSuccess: async (_, variables) => {
      queryClient.resetQueries();
      await router.invalidate();
      notificationForm.reset(variables);
      toast.success("Benachrichtigungseinstellungen gespeichert");
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const changeEmailMutation = useMutation({
    mutationFn: async (newEmail: string) => {
      const callbackURL =
        typeof window !== "undefined"
          ? `${window.location.origin}/dashboard/user?emailChanged=1`
          : "/dashboard/user?emailChanged=1";
      const { data: response, error } = await authClient.changeEmail({
        newEmail: newEmail.trim().toLowerCase(),
        callbackURL,
      });
      if (error) {
        throw createAuthError(error, getChangeEmailErrorMessage(error), "user-update");
      }
      return response;
    },
    onSuccess: (_, newEmail) => {
      queryClient.resetQueries();
      router.invalidate();
      setNewEmail("");
      setShowChangeEmailForm(false);
      toast.success(
        `Wir haben eine E-Mail an ${newEmail} gesendet. Bitte klicken Sie den Link, um die neue Adresse zu bestätigen.`
      );
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const { isPending: isLoading, error, isSuccess } = updateUserMutation;

  return (
    <div className="w-full mb-6">
      <PageHeader title="Benutzerkonto bearbeiten" />
      {user?.email && (
        <div className="mb-6 flex w-full flex-wrap items-center justify-between gap-3">
          <div>
            <Label className="text-muted-foreground">Aktuelle E-Mail-Adresse</Label>
            <p className="mt-1 text-sm font-medium" aria-readonly>
              {user.email}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowChangeEmailForm((prev) => !prev)}
          >
            {showChangeEmailForm ? "Abbrechen" : "E-Mail-Adresse ändern"}
          </Button>
        </div>
      )}
      {showChangeEmailForm && (
        <div className="mb-8 flex w-full flex-col gap-4 rounded-lg border p-4">
          <Label>Neue E-Mail-Adresse</Label>
          <p className="text-muted-foreground text-sm">
            Geben Sie Ihre neue E-Mail-Adresse ein. Wir senden Ihnen einen Bestätigungslink an die neue Adresse.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[200px] flex-1">
              <Input
                type="email"
                placeholder="Neue E-Mail-Adresse"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                id="newEmail"
                disabled={changeEmailMutation.isPending}
                autoComplete="email"
              />
            </div>
            <Button
              type="button"
              variant="secondary"
              disabled={!newEmail.trim() || changeEmailMutation.isPending}
              onClick={() => changeEmailMutation.mutate(newEmail.trim())}
            >
              {changeEmailMutation.isPending ? (
                <>
                  <LoaderIcon className="animate-spin" />
                  Wird gesendet...
                </>
              ) : (
                "Bestätigungslink senden"
              )}
            </Button>
          </div>
        </div>
      )}
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="flex flex-col items-start gap-6 w-full"
          noValidate
        >
          <FormField
            control={form.control}
            name="company"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Firmenname (optional)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    id="company"
                    name="company"
                    type="text"
                    maxLength={50}
                    placeholder="Firmenname"
                  />
                </FormControl>
                <FormMessage className="text-red-500" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="salutation"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Anrede*</FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    className="flex gap-8 mt-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="frau" id="frau" />
                      <Label htmlFor="frau">Frau</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="herr" id="herr" />
                      <Label htmlFor="herr">Herr</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="divers" id="divers" />
                      <Label htmlFor="divers">Divers</Label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage className="text-red-500" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Vorname *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    id="firstName"
                    name="firstName"
                    type="text"
                    placeholder="Vorname"
                  />
                </FormControl>
                <FormMessage className="text-red-500" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Nachname *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    id="lastName"
                    name="lastName"
                    type="text"
                    placeholder="Nachname"
                  />
                </FormControl>
                <FormMessage className="text-red-500" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="telephone"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Telefonnummer</FormLabel>
                <FormControl>
                  <PhoneInput {...field} id="telephone" />
                </FormControl>
                <FormMessage className="text-red-500" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="preferredLanguage"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormLabel>Sprache *</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={field.onChange}
                    {...field}
                    name="preferredLanguage"
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="german">Deutsch</SelectItem>
                      {/* <SelectItem value="english">Englisch</SelectItem> */}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage className="text-red-500" />
              </FormItem>
            )}
          />

          <div className="flex justify-end gap-3 w-full mt-4">
            <Button
              type="reset"
              variant={"outline"}
              onClick={() => form.reset()}
            >
              Abbrechen
            </Button>
            <Button type="submit" variant="default">
              {isLoading ? (
                <>
                  <LoaderIcon className="animate-spin" />
                  Loading...
                </>
              ) : (
                "Speichern"
              )}
            </Button>
          </div>
        </form>
      </Form>

      {/* <Form {...notificationForm}>
        <form
          onSubmit={notificationForm.handleSubmit(handleNotificationSubmit)}
          className="mt-12 flex w-full flex-col"
          noValidate
        >
          <section className="flex flex-col gap-3 border-b border-neutral-200 pb-10">
            <h2 className="text-base font-bold text-foreground">
              Wie möchtest du benachrichtigt werden?
            </h2>
            <p className="text-muted-foreground text-sm leading-snug">
              Wähle die Kanäle, über die du Benachrichtigungen erhalten möchtest.
            </p>
            <div className="flex flex-col gap-4 pt-1">
              <FormField
                control={notificationForm.control}
                name="emailNotification"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-3">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="cursor-pointer font-normal text-foreground">
                      E-Mail-Benachrichtigung
                    </FormLabel>
                  </FormItem>
                )}
              />
              <FormField
                control={notificationForm.control}
                name="desktopNotification"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-3">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="cursor-pointer font-normal text-foreground">
                      Desktop-Benachrichtigung
                    </FormLabel>
                  </FormItem>
                )}
              />
              <FormField
                control={notificationForm.control}
                name="mobilePushNotification"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-3">
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel className="cursor-pointer font-normal text-foreground">
                      Mobile Push-Benachrichtigung
                    </FormLabel>
                  </FormItem>
                )}
              />
            </div>
          </section>

          <section className="flex flex-col gap-3 border-b border-neutral-200 py-10">
            <h2 className="text-base font-bold text-foreground">
              Welche Benachrichtigungen möchtest du bekommen?
            </h2>
            <p className="text-muted-foreground text-sm leading-snug">
              Aktiviere die Kategorien, die für dich relevant sind.
            </p>
            <FormField
              control={notificationForm.control}
              name="newLocationsInMyCity"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center gap-3 pt-1">
                  <FormControl>
                    <Checkbox
                      id="newLocationsInMyCity"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel
                    htmlFor="newLocationsInMyCity"
                    className="cursor-pointer font-normal text-foreground"
                  >
                    Neue Locations in meiner Stadt
                  </FormLabel>
                </FormItem>
              )}
            />
          </section>

          <section className="flex flex-col gap-3 py-10">
            <h2 className="text-base font-bold text-foreground">
              Wie oft möchtest du benachrichtigt werden?
            </h2>
            <p className="text-muted-foreground text-sm leading-snug">
              Lege die Häufigkeit deiner Benachrichtigungs-Zusammenfassung fest.
            </p>
            <FormField
              control={notificationForm.control}
              name="frequency"
              render={({ field }) => (
                <FormItem className="w-full pt-1">
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      className="flex flex-row gap-8"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="weekly" id="frequency-weekly" />
                        <Label htmlFor="frequency-weekly" className="font-normal text-foreground">
                          Wöchentlich
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="monthly" id="frequency-monthly" />
                        <Label htmlFor="frequency-monthly" className="font-normal text-foreground">
                          Monatlich
                        </Label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                </FormItem>
              )}
            />
          </section>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              disabled={updateNotificationMutation.isPending}
              onClick={() => notificationForm.reset()}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={updateNotificationMutation.isPending}
            >
              {updateNotificationMutation.isPending ? (
                <>
                  <LoaderIcon className="animate-spin" />
                  Wird gespeichert...
                </>
              ) : (
                "Speichern"
              )}
            </Button>
          </div>
        </form>
      </Form> */}
    </div>
  );
}
